import ApiService from './';
import { PushProspectToPipelineParams, SearchD7ProspectsParams, SearchLSProspectsParams } from '../params/prospect';

/**
 * @heading1: Prospect API service.
 * @paragraph: This module provides a set of methods to interact with the Prospect API.
 * It handles operations like searching prospects, retrieving all prospects, and other prospect-related tasks.
 */

const ProspectApi = {
    /**
     * @heading3: Search Prospects
     * @paragraph: Sends a POST request to search for prospect based on provided parameters.
     * @param: {string} token - The user's authentication token.
     * @param: {SearchProspectsParams} data - The parameters required to filter and search prospects.
     * @returns: {Promise<unknown>} - The response containing the filtered prospect details.
     * @separator:
     */
    searchProspects: (token: string, data: SearchD7ProspectsParams | SearchLSProspectsParams) => {
        // Perform a POST request to the prospect search endpoint with the provided data
        return ApiService.apiHit({
            url: 'prospects',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: Get All Prospects
     * @paragraph: Sends a GET request to retrieve all prospects available for the user.
     * @param: {string} token - The user's authentication token.
     * @returns: {Promise<unknown[]>} - The response containing a list of all prospects.
     * @separator:
     */
    getAllProspects: (token: string) => {
        // Perform a GET request to the prospects endpoint with the token
        return ApiService.apiHit({
            url: 'prospects',
            method: 'get',
            token
        });
    },

    /**
     * @heading3: Get Single Prospect Details
     * @paragraph: Sends a GET request to retrieve prospect details available for the user.
     * @param: {string} token - The user's authentication token.
     * @returns: {Promise<unknown[]>} - The response containing a object of detail of prospect.
     * @separator:
     */
    getProspectDetail: (token: string, prospect_id: number) => {
        // Perform a GET request to the prospects endpoint with the token
        return ApiService.apiHit({
            url: 'prospect-details',
            method: 'post',
            token,
            data: {
                prospect_id
            },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },

    /**
     * @heading3: Prospect push to pipeline
     * @paragraph: Sends a POST request to push a prospect into a specified pipeline.
     * This operation associates a prospect with a pipeline and stage based on the provided data.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {PushProspectToPipelineParams} data - The data containing the prospect details, pipeline ID, and stage ID for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    pushProspectToPipeline: (token: string, data: PushProspectToPipelineParams) => {
        // Perform a Post request to push prospect to pipeline endpoint with the token
        return ApiService.apiHit({
            url: 'prospect-push-to-pipeline',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Prospect push to pipeline
     * @paragraph: Sends a POST request to push a prospect into a specified pipeline.
     * This operation associates a prospect with a pipeline and stage based on the provided data.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {PushProspectToPipelineParams} data - The data containing the prospect details, pipeline ID, and stage ID for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    fetchSuggestions: (token: string) => {
        // Perform a Post request to push prospect to pipeline endpoint with the token
        return ApiService.apiHit({
            url: 'get-suggestion',
            method: 'get',
            token
        });
    },
    /**
     * @heading3: Prospect push to pipeline
     * @paragraph: Sends a POST request to push a prospect into a specified pipeline.
     * This operation associates a prospect with a pipeline and stage based on the provided data.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {PushProspectToPipelineParams} data - The data containing the prospect details, pipeline ID, and stage ID for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    deleteProspect: (token: string, prospect_id: number) => {
        // Perform a Post request to push prospect to pipeline endpoint with the token
        return ApiService.apiHit({
            url: 'delete-prospect',
            method: 'post',
            token,
            data: { prospect_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Prospect push to pipeline
     * @paragraph: Sends a POST request to push a prospect into a specified pipeline.
     * This operation associates a prospect with a pipeline and stage based on the provided data.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {PushProspectToPipelineParams} data - The data containing the prospect details, pipeline ID, and stage ID for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    deleteProspectItem: (token: string, prospect_result_id: number) => {
        // Perform a Post request to push prospect to pipeline endpoint with the token
        return ApiService.apiHit({
            url: 'delete-prospect-item',
            method: 'post',
            token,
            data: { prospect_result_id },
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    /**
     * @heading3: Prospect push to pipeline
     * @paragraph: Sends a POST request to push a prospect into a specified pipeline.
     * This operation associates a prospect with a pipeline and stage based on the provided data.
     * @param: {string} token - The user's authentication token, required for authorization.
     * @param: {PushProspectToPipelineParams} data - The data containing the prospect details, pipeline ID, and stage ID for the operation.
     * @returns: {Promise<unknown[]>} - A promise that resolves to the response containing the operation result.
     * @separator:
     */
    fetchProspect: (token: string) => {
        // Perform a Post request to push prospect to pipeline endpoint with the token
        return ApiService.apiHit({
            url: 'fetch-prospects',
            method: 'get',
            token
        });
    },
    uploadManualProspect: (token: string, data: {
        csv_file: string;
        niche: string;
        city: string;
        state: string;
        country: string;
    }) => {
        // Perform a Post request to push prospect to pipeline endpoint with the token
        return ApiService.apiHit({
            url: 'upload-manual-prospect',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    },
    createManualProspect: (token: string, data: {
        temp_token: string;
        mapping_data: string;
    }) => {
        // Perform a Post request to push prospect to pipeline endpoint with the token
        return ApiService.apiHit({
            url: 'custom-prospect-save',
            method: 'post',
            token,
            data,
            getIp: {
                get: true,
                fieldValue: 'user_ip'
            }
        });
    }
};

export default ProspectApi;
